import { supabase } from "@/integrations/supabase/client";
import type { Prompt } from "@/services/promptService";

interface SearchWeights {
  title: number;
  description: number;
  content: number;
  tags: number;
}

interface SearchOptions {
  categoryFilter?: string;
  difficultyFilter?: string;
  timeFilter?: string;
  tagFilters?: string[];
  sortBy?: 'relevance' | 'popular' | 'recent' | 'alphabetical';
  limit?: number;
}

interface SearchResult extends Prompt {
  relevanceScore: number;
  matchedFields: string[];
  snippet: string;
}

// Default search weights for different fields
const DEFAULT_WEIGHTS: SearchWeights = {
  title: 3.0,
  description: 2.0,
  content: 1.0,
  tags: 2.5
};

// Common synonyms for prompt template terms
const SYNONYMS: Record<string, string[]> = {
  'prd': ['product requirement', 'product requirements document', 'product spec'],
  'product requirement': ['prd', 'product spec', 'product requirements document'],
  'brainstorm': ['ideation', 'idea generation', 'creative thinking'],
  'research': ['analysis', 'investigation', 'study'],
  'customer': ['user', 'client', 'end user'],
  'feedback': ['review', 'opinion', 'input'],
  'market': ['business', 'commercial', 'industry']
};

export class EnhancedSearchService {
  private static expandQueryWithSynonyms(query: string): string[] {
    const words = query.toLowerCase().split(/\s+/);
    const expandedTerms = new Set<string>();
    
    // Add original query
    expandedTerms.add(query.toLowerCase());
    
    // Add synonyms for each word
    words.forEach(word => {
      expandedTerms.add(word);
      if (SYNONYMS[word]) {
        SYNONYMS[word].forEach(synonym => expandedTerms.add(synonym));
      }
    });
    
    return Array.from(expandedTerms);
  }

  private static calculateRelevanceScore(
    prompt: any,
    searchTerms: string[],
    weights: SearchWeights = DEFAULT_WEIGHTS
  ): { score: number; matchedFields: string[] } {
    let totalScore = 0;
    const matchedFields: string[] = [];
    
    const title = (prompt.title || '').toLowerCase();
    const description = (prompt.description || '').toLowerCase();
    const content = (prompt.content || '').toLowerCase();
    const category = (prompt.categories?.name || '').toLowerCase();
    
    searchTerms.forEach(term => {
      const termLower = term.toLowerCase();
      
      // Title matches
      if (title.includes(termLower)) {
        totalScore += weights.title;
        if (!matchedFields.includes('title')) matchedFields.push('title');
      }
      
      // Description matches
      if (description.includes(termLower)) {
        totalScore += weights.description;
        if (!matchedFields.includes('description')) matchedFields.push('description');
      }
      
      // Content matches
      if (content.includes(termLower)) {
        totalScore += weights.content;
        if (!matchedFields.includes('content')) matchedFields.push('content');
      }
      
      // Category matches
      if (category.includes(termLower)) {
        totalScore += weights.tags;
        if (!matchedFields.includes('category')) matchedFields.push('category');
      }
    });
    
    // Boost score for exact phrase matches in title
    searchTerms.forEach(term => {
      if (term.length > 3 && title.includes(term.toLowerCase())) {
        totalScore += weights.title * 0.5; // Bonus for exact phrase
      }
    });
    
    return { score: totalScore, matchedFields };
  }

  private static generateSnippet(
    prompt: any,
    searchTerms: string[],
    maxLength: number = 150
  ): string {
    const description = prompt.description || '';
    const content = prompt.content || '';
    
    // Try to find snippet with search terms
    for (const term of searchTerms) {
      const termLower = term.toLowerCase();
      const descIndex = description.toLowerCase().indexOf(termLower);
      const contentIndex = content.toLowerCase().indexOf(termLower);
      
      if (descIndex !== -1) {
        const start = Math.max(0, descIndex - 50);
        const end = Math.min(description.length, start + maxLength);
        const snippet = description.substring(start, end);
        return start > 0 ? '...' + snippet : snippet;
      }
      
      if (contentIndex !== -1) {
        const start = Math.max(0, contentIndex - 50);
        const end = Math.min(content.length, start + maxLength);
        const snippet = content.substring(start, end);
        return start > 0 ? '...' + snippet : snippet;
      }
    }
    
    // Fallback to description or content start
    const text = description || content;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  private static buildSearchQuery(searchTerms: string[], options: SearchOptions) {
    let query = supabase
      .from('prompts')
      .select(`
        id, 
        title, 
        description, 
        content,
        likes,
        reviews,
        copies,
        created_at,
        user_id,
        category_id,
        status,
        categories(id, name, slug)
      `)
      .eq('status', 'approved');
    
    // Build OR condition for all search terms across multiple fields
    if (searchTerms.length > 0) {
      const searchConditions = searchTerms.map(term => 
        `title.ilike.%${term}%,description.ilike.%${term}%,content.ilike.%${term}%`
      ).join(',');
      
      query = query.or(searchConditions);
    }
    
    // Apply category filter
    if (options.categoryFilter) {
      query = query.eq('category_id', options.categoryFilter);
    }
    
    // Apply limit
    if (options.limit) {
      query = query.limit(options.limit);
    }
    
    return query;
  }

  static async searchPrompts(
    searchQuery: string,
    options: SearchOptions = {}
  ): Promise<SearchResult[]> {
    if (!searchQuery.trim() && !options.categoryFilter) {
      return [];
    }
    
    try {
      const searchTerms = searchQuery ? this.expandQueryWithSynonyms(searchQuery) : [];
      const query = this.buildSearchQuery(searchTerms, options);
      
      const { data, error } = await query;
      
      if (error) throw error;
      if (!data || data.length === 0) return [];
      
      // Get unique user IDs for author information
      const userIds = data.map(item => item.user_id).filter(Boolean);
      let profileMap: Map<string, any> = new Map();
      
      if (userIds.length > 0) {
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .in('id', userIds);
        
        if (!profileError && profiles) {
          profiles.forEach(profile => {
            profileMap.set(profile.id, profile);
          });
        }
      }
      
      // Calculate relevance scores and format results
      const searchResults: SearchResult[] = data.map(item => {
        const profile = profileMap.get(item.user_id) || {};
        const { score, matchedFields } = searchTerms.length > 0 
          ? this.calculateRelevanceScore(item, searchTerms)
          : { score: 0, matchedFields: [] };
        
        return {
          id: item.id,
          title: item.title,
          description: item.description,
          content: item.content,
          category: item.categories?.name || 'Uncategorized',
          category_id: item.category_id,
          likes: item.likes || 0,
          reviews: item.reviews || 0,
          copies: item.copies || 0,
          status: item.status,
          author: {
            id: item.user_id || 'anonymous',
            name: [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Anonymous'
          },
          createdAt: item.created_at,
          relevanceScore: score,
          matchedFields,
          snippet: this.generateSnippet(item, searchTerms)
        };
      });
      
      // Sort by relevance (if we have search terms) or by specified sort option
      if (searchTerms.length > 0 && options.sortBy !== 'recent' && options.sortBy !== 'alphabetical') {
        searchResults.sort((a, b) => b.relevanceScore - a.relevanceScore);
      } else {
        switch (options.sortBy) {
          case 'popular':
            searchResults.sort((a, b) => (b.copies + b.likes) - (a.copies + a.likes));
            break;
          case 'recent':
            searchResults.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            break;
          case 'alphabetical':
            searchResults.sort((a, b) => a.title.localeCompare(b.title));
            break;
          default:
            // Keep relevance order
            break;
        }
      }
      
      return searchResults;
    } catch (error: any) {
      console.error('Error in enhanced search:', error);
      throw error;
    }
  }

  static async getSearchSuggestions(query: string, limit: number = 5): Promise<string[]> {
    if (query.length < 2) return [];
    
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select('title')
        .eq('status', 'approved')
        .ilike('title', `%${query}%`)
        .limit(limit);
      
      if (error) throw error;
      
      const suggestions = data?.map(item => item.title) || [];
      
      // Add synonym suggestions
      const synonymSuggestions: string[] = [];
      Object.keys(SYNONYMS).forEach(key => {
        if (key.includes(query.toLowerCase())) {
          synonymSuggestions.push(key);
          SYNONYMS[key].forEach(synonym => {
            if (synonym.includes(query.toLowerCase())) {
              synonymSuggestions.push(synonym);
            }
          });
        }
      });
      
      return [...new Set([...suggestions, ...synonymSuggestions])].slice(0, limit);
    } catch (error) {
      console.error('Error getting search suggestions:', error);
      return [];
    }
  }
}
